import http, {setAuthToken} from "./httpService.ts";
import type {ILoginResponse} from "../models/interfaces/loginResponse.interface.ts";
import type {OAuthProvider} from "./oauthProviders.ts";
import {InvalidProviderLoginResponseError} from "../utils/loginErrors.ts";

// Login with email and password
export const login = async (username: string, password: string): Promise<ILoginResponse> => {
  try {
    const credentials = btoa(`${username}:${password}`);
    const response = await http.post("/api/v1/users/auth/login", null, {
      headers: {
        Authorization: `Basic ${credentials}`,
      },
    });

    // Save JWT token to localStorage
    const loginResponse: ILoginResponse = response.data as ILoginResponse;
    const token: string = loginResponse.jwt;
    localStorage.setItem("authToken", token);

    // Set the token for future requests
    setAuthToken(token);

    return loginResponse;
  } catch (err) {
    console.error("Login failed:", err);
    throw err;
  }
};

export const loginWithProvider = async (
  provider: OAuthProvider,
  credential: string
): Promise<ILoginResponse> => {
  const endpoint =
    provider === "google"
      ? "/api/v1/users/auth/login/oauth/google"
      : "/api/v1/users/auth/login/oauth/microsoft";
  const payload = provider === "google" ? {code: credential} : {idToken: credential};
  const response = await http.post<ILoginResponse>(endpoint, payload);
  const loginResponse = response.data;

  if (
    typeof loginResponse.jwt !== "string" ||
    loginResponse.jwt === "" ||
    !loginResponse.user ||
    typeof loginResponse.user !== "object"
  ) {
    throw new InvalidProviderLoginResponseError();
  }

  const token = loginResponse.jwt;
  localStorage.setItem("authToken", token);
  setAuthToken(token);

  return loginResponse;
};

// Refresh token function
export const refreshToken = async (): Promise<ILoginResponse> => {
  const response = await http.put("/api/v1/users/auth/me/refresh-token");
  const token = response.data.jwt;
  localStorage.setItem("authToken", token);
  setAuthToken(token);

  return response.data;
};

// Logout function
export const logout = () => {
  localStorage.removeItem("authToken");
  setAuthToken(null);
};

// Get token from localStorage
export const getAuthToken = () => {
  return localStorage.getItem("authToken");
};
